import {
    ActionEntry,
    CondEntry,
    ConditionGroupPayload,
    ConditionOperator,
    ConditionType,
    GroupNode,
    LogicOp,
    OPERATORS_BY_TYPE,
    RuleAction,
} from 'Core-utils/types/rules.types';

// ─── Local ID counter — unique within this module's lifetime, O(1) ────────────
let _id = 0;
export const nextId = () => `rb_${++_id}`;

// ─── Factories ────────────────────────────────────────────────────────────────
export const emptyCond = (): CondEntry => ({
    kind: 'cond',
    id: nextId(),
    fieldPath: '',
    fieldType: 'string',
    operator: OPERATORS_BY_TYPE.string[0].value,
    value: '',
});

export const emptyGroup = (): GroupNode => ({
    kind: 'group',
    id: nextId(),
    logic: 'and',
    children: [emptyCond()],
});

export const emptyAction = (): ActionEntry => ({ id: nextId(), actionType: 'ADD_TAG' });

// ─── Payload serialisers ──────────────────────────────────────────────────────

/** Convert a single CondEntry to the condition shape expected by the API */
export const condEntryToPayload = (c: CondEntry) => ({
    type: c.fieldType,
    name: c.fieldPath,
    operator: c.operator,
    value: c.value,
});

/**
 * Recursively convert a GroupNode into the API conditionGroup shape:
 *   { behavior, sort_order, conditions, childGroups }
 */

export function groupNodeToConditionGroup(node: GroupNode, sortOrder: number): ConditionGroupPayload {
    const conditions = node.children
        .filter((c) => c.kind === 'cond')
        .map((c) => condEntryToPayload(c as CondEntry));
    const childGroups = node.children
        .filter((c) => c.kind === 'group')
        .map((c, i) => groupNodeToConditionGroup(c as GroupNode, i));
    return {
        behavior: node.logic === 'and' ? 'all' : 'any',
        // eslint-disable-next-line camelcase
        sort_order: sortOrder,
        conditions,
        childGroups,
    };
}

/**
 * Build the conditionGroups array for advanced mode.
 * Root-level CondEntry items are collected into an implicit "all" group.
 */
export function advancedToConditionGroups(children: Array<CondEntry | GroupNode>) {
    const rootConds: CondEntry[] = [];
    const groups: ConditionGroupPayload[] = [];
    let groupIdx = 0;

    children.forEach((child) => {
        if (child.kind === 'cond') {
            rootConds.push(child);
        } else {
            groups.push(groupNodeToConditionGroup(child, groupIdx++));
        }
    });

    if (rootConds.length > 0) {
        return [
            // eslint-disable-next-line camelcase
            { behavior: 'all', sort_order: 0, conditions: rootConds.map(condEntryToPayload), childGroups: [] },
            // eslint-disable-next-line camelcase
            ...groups.map((g, i) => ({ ...g, sort_order: i + 1 })),
        ];
    }
    return groups;
}

/** Build the conditionGroups array for simple mode (always a single group, no childGroups) */
export const simpleToConditionGroups = (conds: CondEntry[], behavior: LogicOp) => [
    {
        behavior: behavior === 'and' ? 'all' : 'any',
        // eslint-disable-next-line camelcase
        sort_order: 0,
        conditions: conds.map(condEntryToPayload),
        childGroups: [],
    },
];

/**
 * Recursively convert API conditionGroups back into the GroupNode tree.
 * Used when loading a rule in edit mode.
 */
export function conditionGroupsToState(groups: ConditionGroupPayload[]): GroupNode[] {
    return groups.map((cg: ConditionGroupPayload) => ({
        kind: 'group' as const,
        id: nextId(),
        logic: (cg.behavior === 'all' ? 'and' : 'or') as LogicOp,
        children: [
            ...(cg.conditions ?? []).map((c) => ({
                kind: 'cond' as const,
                id: nextId(),
                fieldPath: c.name ?? '',
                fieldType: (c.type ?? 'string') as ConditionType,
                operator: c.operator as ConditionOperator,
                value: c.value,
            } as CondEntry)),
            ...conditionGroupsToState(cg.childGroups ?? []),
        ],
    }));
}

export const actionsToApi = (then: ActionEntry[], els: ActionEntry[]): RuleAction[] => [
    // eslint-disable-next-line camelcase
    ...then.map((a) => ({ action_type: 'then', action_data: { type: a.actionType } } as RuleAction)),
    // eslint-disable-next-line camelcase
    ...els.map((a)  => ({ action_type: 'else', action_data: { type: a.actionType } } as RuleAction)),
];

export const condSummaryText = (c: CondEntry): string => {
    const op = OPERATORS_BY_TYPE[c.fieldType]?.find((o) => o.value === c.operator)?.label ?? c.operator;
    const val = Array.isArray(c.value) ? c.value.join(', ') : String(c.value ?? '');
    return `${c.fieldPath} ${op} ${val}`;
};

export const tryPretty = (v: unknown): string => {
    if (!v && v !== 0) {
        return '';
    }
    try {
        const obj = typeof v === 'string' ? JSON.parse(v) : v;
        return JSON.stringify(obj, null, 2);
    } catch {
        return typeof v === 'string' ? v : JSON.stringify(v);
    }
};

export const tryParse = (s: string): { value: unknown; error: string | null } => {
    const t = s.trim();
    if (!t) {
        return { value: undefined, error: null };
    }
    try {
        return { value: JSON.parse(t), error: null };
    } catch (e) {
        return { value: s, error: (e as Error).message };
    }
};

export const isJsony = (v: unknown): boolean =>
    v !== null &&
    typeof v === 'object' &&
    (Array.isArray(v) || Object.prototype.toString.call(v) === '[object Object]');

export function syntaxHighlight(json: string): string {
    return json
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(
            /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
            (match) => {
                let cls = 'syntax-hl--value';
                if (/^"/.test(match)) {
                    cls = /:$/.test(match) ? 'syntax-hl--key' : 'syntax-hl--string';
                } else if (/true|false/.test(match)) {
                    cls = 'syntax-hl--boolean';
                } else if (/null/.test(match)) {
                    cls = 'syntax-hl--null';
                }
                return `<span class="${cls}">${match}</span>`;
            },
        );
}

/**
 * Operators whose value is a list, so the builder collects chips rather than a
 * single value. `contains` is deliberately absent: RulesEngine implements it as
 * a single-needle substring test (`x.includes(y)` with `y` a string), so
 * collecting a list here produced a value the engine could never match.
 * `contains_any` is the list form.
 */
export const isArrayOperator = (op: string) =>
    ['in', 'not_in', 'contains_any', 'not_contains_any',
     'contains_all', 'between', 'not_between', 'has_any', 'has_all'].includes(op);

export const toItems = (arr: { label: string; value: string }[]) =>
    arr.map((x) => ({ id: x.value, displayValue: x.label, value: x.value }));